import React, { useState } from "react";
import { supabase } from "@/lib/supabase";

const YOUTUBE_API_KEY = "AIzaSyAimM3FIehnFk1MPunKWwylJaGMiFYIokQ";

function extractPlaylistId(url: string): string | null {
  try {
    const parsed = new URL(url);
    return parsed.searchParams.get("list");
  } catch {
    return null;
  }
}

export default function ImportPlaylist() {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);

  const handleImport = async () => {
    setLoading(true);
    const playlistId = extractPlaylistId(url);
    if (!playlistId) {
      alert("Invalid playlist URL.");
      setLoading(false);
      return;
    }

    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError || !user) {
      alert("You must be logged in to import a playlist.");
      setLoading(false);
      return;
    }

    // Fetch playlist title
    const playlistInfoRes = await fetch(
      `https://www.googleapis.com/youtube/v3/playlists?part=snippet&id=${playlistId}&key=${YOUTUBE_API_KEY}`
    );
    const playlistInfoData = await playlistInfoRes.json();
    const playlistTitle = playlistInfoData.items?.[0]?.snippet?.title || `Import: ${playlistId}`;

    let nextPageToken = "";
    const videos = [];

    try {
      // Fetch playlist items
      while (true) {
        const res = await fetch(
          `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&maxResults=50&playlistId=${playlistId}&key=${YOUTUBE_API_KEY}&pageToken=${nextPageToken}`
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error?.message || "Failed to fetch playlist items");

        for (const item of data.items) {
          const s = item.snippet;
          videos.push({
            id: s.resourceId.videoId,
            title: s.title,
            description: s.description,
            thumbnail: s.thumbnails?.medium?.url || "",
          });
        }

        if (!data.nextPageToken) break;
        nextPageToken = data.nextPageToken;
      }

      // Insert collection
      const { data: newCollection, error: insertError } = await supabase
        .from("collections")
        .insert({
          name: playlistTitle,
          is_public: true,
          user_id: user.id,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Upsert videos
      await Promise.all(
        videos.map(async (video) => {
          await supabase.from("videos").upsert({
            id: video.id,
            title: video.title,
            thumbnail: video.thumbnail,
            created_at: new Date().toISOString(),
          });
        })
      );

      // Link videos to collection
      const { error: linkError } = await supabase.from("collection_videos").insert(
        videos.map((video) => ({
          collection_id: newCollection.id,
          video_id: video.id,
          title: video.title,
          thumbnail: video.thumbnail,
        }))
      );

      if (linkError) throw linkError;

      alert("Playlist imported successfully!");
      setUrl("");
    } catch (err: any) {
      console.error(err);
      alert("Import error: " + err.message);
    }

    setLoading(false);
  };

  return (
    <div className="p-4 border border-border rounded bg-muted shadow max-w-md">
      <h2 className="text-lg font-semibold text-foreground mb-2">
        Import YouTube Playlist
      </h2>
      <input
        type="text"
        className="w-full bg-background text-foreground border border-border px-3 py-2 mb-3 rounded"
        placeholder="https://www.youtube.com/playlist?list=..."
        value={url}
        onChange={(e) => setUrl(e.target.value)}
      />
      <button
        className="bg-primary text-primary-foreground px-4 py-2 rounded hover:bg-primary/90 disabled:opacity-50"
        onClick={handleImport}
        disabled={loading}
      >
        {loading ? "Importing..." : "Import"}
      </button>
    </div>
  );
}